import { useCallback, useEffect, useMemo, useState } from 'react';
import { patientApi, type ProviderSlot } from '../../api';

/**
 * A provider's real published openings, for every booking screen.
 *
 * Before this hook each screen drew its own 09:00–18:00 grid and marked every
 * time "available" — times nobody had checked, for doctors who often publish
 * no schedule at all. `GET /providers/:idOrSlug/slots` answers with what the
 * doctor actually published; `hasSchedule: false` means there is nothing to
 * read, and the screen must then ask for a *preferred* time and say so.
 */

/** YYYY-MM-DD for a date offset from today, in the local calendar. */
export function dateKeyFromOffset(offsetDays: number): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** "18:00" → "6:00 PM" */
export function formatSlotTime(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  if (!Number.isFinite(h)) return hhmm;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m ?? 0).padStart(2, '0')} ${period}`;
}

/**
 * The times a patient may state as a preference when a provider publishes no
 * schedule. They are NOT availability and every screen using them says so.
 */
export function preferredTimeGrid(): string[] {
  const out: string[] = [];
  for (let h = 9; h <= 18; h++) {
    for (const m of ['00', '30']) out.push(`${String(h).padStart(2, '0')}:${m}`);
  }
  return out;
}

export interface ProviderSlotsState {
  loading: boolean;
  error: string;
  /** False when the provider publishes no schedule — preference mode. */
  hasSchedule: boolean;
  /** Published openings keyed by YYYY-MM-DD, each sorted by start time. */
  byDate: Record<string, ProviderSlot[]>;
  /** Dates (YYYY-MM-DD) that have at least one opening left. */
  openDates: string[];
  slotsFor: (date: string) => ProviderSlot[];
  refresh: () => void;
}

export function useProviderSlots(providerIdOrSlug: string | undefined, days = 14): ProviderSlotsState {
  const [loading, setLoading] = useState(!!providerIdOrSlug);
  const [error, setError] = useState('');
  const [hasSchedule, setHasSchedule] = useState(false);
  const [slots, setSlots] = useState<ProviderSlot[]>([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!providerIdOrSlug) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    setError('');
    patientApi.getProviderSlots(providerIdOrSlug, days)
      .then(res => {
        if (!alive) return;
        setHasSchedule(!!res?.hasSchedule);
        setSlots(Array.isArray(res?.slots) ? res.slots : []);
      })
      .catch((e: any) => {
        if (!alive) return;
        // Availability could not be read. The screen falls back to asking for
        // a preferred time — it must never pretend the times are confirmed.
        setHasSchedule(false);
        setSlots([]);
        setError(e?.body?.message || 'Live availability could not be loaded.');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [providerIdOrSlug, days, tick]);

  const byDate = useMemo(() => {
    const map: Record<string, ProviderSlot[]> = {};
    for (const s of slots) (map[s.date] ??= []).push(s);
    for (const list of Object.values(map)) list.sort((a, b) => a.startTime.localeCompare(b.startTime));
    return map;
  }, [slots]);

  const openDates = useMemo(() => Object.keys(byDate).sort(), [byDate]);
  const slotsFor = useCallback((date: string) => byDate[date] ?? [], [byDate]);
  const refresh = useCallback(() => setTick(t => t + 1), []);

  return { loading, error, hasSchedule, byDate, openDates, slotsFor, refresh };
}
